import { Divider, Space } from 'antd';
import { useIntl } from 'react-intl';

import { applyMask } from '@/utils/mask';
import { cepMask } from '@/utils/mask/commonMasks';
import DeleteIcon from '@/components/_common/DeleteIcon';
import EditIcon from '@/components/_common/EditIcon';
import { InsurerAddress } from '@/core/domain/people/InsurerAddress/InsurersAddress.types';
import Table, { ColumnsType } from 'antd/lib/table';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

interface Props {
  isLoading: boolean;
  onEdit: (record: InsurerAddress) => void;
  onRemove: (record: InsurerAddress) => void;
  address: InsurerAddress[];
}

const InsurerAddressesList = ({
  isLoading,
  onEdit,
  onRemove,
  address,
}: Props) => {
  const intl = useIntl();

  const columns: ColumnsType<InsurerAddress> = [
    {
      title: intl.formatMessage({
        id: 'insurers.addresses.list.header.zipCode',
      }),
      key: 'zipCode',
      dataIndex: 'zipCode',
      width: COLUMNS_WIDTH.document,
      render: (value) => applyMask(value, cepMask),
    },
    {
      title: intl.formatMessage({
        id: 'insurers.addresses.list.header.street',
      }),
      key: 'street',
      dataIndex: 'street',
      width: 200,
    },
    {
      title: intl.formatMessage({
        id: 'insurers.addresses.list.header.number',
      }),
      key: 'number',
      dataIndex: 'number',
      width: 90,
    },
    {
      width: 90,
      title: intl.formatMessage({
        id: 'insurers.addresses.list.header.main',
      }),
      key: 'main',
      dataIndex: 'main',
      render: (_, record) => (
        <span>
          {intl.formatMessage({
            id: `commons.options.${record.main ? 'yes' : 'no'}`,
          })}
        </span>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'insurers.addresses.list.header.actions',
      }),
      key: 'actions',
      dataIndex: 'actions',
      align: 'right',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => onEdit(record),
            }}
            disabled={!hasPermissions([Authorities.REGISTER_INSURER_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => onRemove(record)}
            disabled={!hasPermissions([Authorities.REGISTER_INSURER_EDIT])}
          />
        </Space>
      ),
    },
  ];

  return (
    <Table<InsurerAddress>
      columns={columns}
      dataSource={address ? address : []}
      loading={isLoading}
      rowKey={(row) => row.id}
      scroll={{ x: getTableScrollX(columns) }}
      pagination={false}
    />
  );
};

export default InsurerAddressesList;
